import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import SearchableDropdown from '../../../components/SearchableDropdown';
import type { AiRegistryResponse } from '../../../services/aiControlApi';
import {
  controlsApi, effortsFor, EFFORT_LABEL,
  type AiEffort, type AiModelChoice, type AiProfile, type AiProfilesDoc,
} from '../../../services/consoleApi';
import { modelOptions, providerOptions } from './routingDraft';
import { Badge, Btn, Card, CardHeader, Toggle } from '../rubyAi/ui';
import { RubyLoader } from '../../../components/RubyLoader';

/*
  DEVELOPER AI PROFILES -- named presets of which Claude model and effort Ruby
  uses in the issue console, so the owner can switch between them and find the
  sweet spot ("Fast", "Balanced", "Deep").

  Each profile: Analysing (model + effort) and Developing (model + effort).
  Both are Claude: Developing runs Claude Code on GitHub; Analysing waits on
  the Gemini caller. One profile is the default; profiles marked "developers
  can use" appear in the console's per-attempt picker. Saved in support-service
  (cfg_dev_ai_profiles); only the owner or a Controls approver can change them.
*/

const blankChoice = (modelId: string, effort: AiEffort | null): AiModelChoice => ({ providerId: 'anthropic', modelId, effort });

const slug = (name: string, taken: Set<string>): string => {
  const base = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30) || 'profile';
  let id = base;
  for (let n = 2; taken.has(id); n++) id = `${base}-${n}`;
  return id;
};

const ChoiceRow: React.FC<{
  label: string;
  hint: string;
  value: AiModelChoice;
  registry: AiRegistryResponse;
  onChange: (c: AiModelChoice) => void;
  disabled?: boolean;
}> = ({ label, hint, value, registry, onChange, disabled }) => {
  const claude = useMemo(() => registry.providers.filter((p) => p.providerId === 'anthropic'), [registry.providers]);
  const efforts = effortsFor(value.providerId, value.modelId);
  return (
    <div className="flex flex-col gap-2">
      <div>
        <span className="text-[13px] font-semibold text-stone-900">{label}</span>
        <span className="text-[12px] text-stone-500 ml-2">{hint}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <span className="block text-[12px] font-medium text-stone-700 mb-1">Provider</span>
          <SearchableDropdown
            options={providerOptions(claude)}
            value={value.providerId || null}
            onChange={(v) => onChange({ providerId: v || '', modelId: '', effort: null })}
            placeholder="Choose a provider"
            ariaLabel={`${label} provider`}
            disabled={disabled}
          />
        </div>
        <div>
          <span className="block text-[12px] font-medium text-stone-700 mb-1">Model</span>
          <SearchableDropdown
            options={modelOptions(registry.models.filter((m) => m.enabled), value.providerId)}
            value={value.modelId || null}
            onChange={(v) => {
              const modelId = v || '';
              const allowed = effortsFor(value.providerId, modelId);
              onChange({ ...value, modelId, effort: value.effort && allowed.includes(value.effort) ? value.effort : (allowed.includes('high') ? 'high' : null) });
            }}
            placeholder={value.providerId ? 'Choose a model' : 'Choose a provider first'}
            searchPlaceholder="Search models…"
            ariaLabel={`${label} model`}
            disabled={disabled || !value.providerId}
          />
        </div>
        <div>
          <span className="block text-[12px] font-medium text-stone-700 mb-1">Effort</span>
          {efforts.length ? (
            <SearchableDropdown
              options={efforts.map((e) => ({ value: e, label: EFFORT_LABEL[e], subtitle: e === 'low' ? 'Fastest, cheapest' : e === 'max' ? 'Deepest, most tokens' : undefined }))}
              value={value.effort || null}
              onChange={(v) => onChange({ ...value, effort: (v as AiEffort) || null })}
              placeholder="Model default"
              ariaLabel={`${label} effort`}
              disabled={disabled}
            />
          ) : (
            <div className="h-11 flex items-center px-3 rounded-lg border border-dashed border-stone-200 text-[12px] text-stone-500">
              {value.modelId ? 'This model has no effort setting' : 'Choose a model first'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const DeveloperAiSection: React.FC<{
  registry: AiRegistryResponse;
  showToast: (m: string, t?: 'success' | 'error' | 'info') => void;
}> = ({ registry, showToast }) => {
  const [doc, setDoc] = useState<AiProfilesDoc | null>(null);
  const [saved, setSaved] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const d = await controlsApi.getAiProfiles();
      const clean = { profiles: d.profiles, defaultId: d.defaultId };
      setDoc(clean);
      setSaved(JSON.stringify(clean));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => { void load(); }, []);

  const dirty = !!doc && JSON.stringify({ profiles: doc.profiles, defaultId: doc.defaultId }) !== saved;
  const patch = (id: string, p: Partial<AiProfile>) =>
    setDoc((d) => d && { ...d, profiles: d.profiles.map((x) => (x.id === id ? { ...x, ...p } : x)) });

  const add = () => setDoc((d) => {
    if (!d) return d;
    const taken = new Set(d.profiles.map((p) => p.id));
    const name = `Profile ${d.profiles.length + 1}`;
    return {
      ...d,
      profiles: [...d.profiles, {
        id: slug(name, taken), name,
        analysis: blankChoice('claude-haiku-4-5-20251001', null),
        develop: blankChoice('claude-opus-5', 'high'),
        forDevelopers: true,
      }],
    };
  });

  const save = async () => {
    if (!doc) return;
    setSaving(true);
    try {
      const r = await controlsApi.saveAiProfiles(doc);
      const clean = { profiles: r.profiles, defaultId: r.defaultId };
      setDoc(clean);
      setSaved(JSON.stringify(clean));
      showToast('Developer AI profiles saved', 'success');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : String(e), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-3" aria-label="Developer AI profiles">
      <Card>
        <CardHeader
          title="Developer AI profiles"
          hint="Which Claude model and effort Ruby uses to analyse and to fix issues. Switch the default to try a different mix; developers can pick any profile marked for them on each attempt. Every run records its profile, so cost and results can be compared."
          actions={doc && (
            <div className="flex gap-2">
              <Btn onClick={add} disabled={saving || doc.profiles.length >= 12}><Plus size={16} /> Add profile</Btn>
              <Btn kind="primary" onClick={save} disabled={!dirty || saving} busy={saving}><Save size={16} /> Save</Btn>
            </div>
          )}
        />
        {error && <div className="p-4 text-[13px] text-red-700">{error}</div>}
        {!doc && !error && <RubyLoader variant="inline" />}
        {doc && doc.profiles.map((p) => {
          const isDefault = doc.defaultId === p.id;
          return (
            <div key={p.id} className="p-4 md:p-5 border-t border-stone-100 flex flex-col gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex flex-col gap-1 min-w-[200px] flex-1 max-w-sm">
                  <span className="text-[12px] font-medium text-stone-700">Profile name</span>
                  <input
                    value={p.name}
                    onChange={(e) => patch(p.id, { name: e.target.value.slice(0, 40) })}
                    className="h-11 rounded-lg border border-stone-300 px-3 text-[14px] text-stone-900 bg-white"
                  />
                </label>
                <div className="flex items-center gap-3 flex-wrap ml-auto">
                  {isDefault
                    ? <Badge tone="rubySolid">Default</Badge>
                    : <Btn onClick={() => setDoc({ ...doc, defaultId: p.id })} className="min-h-[44px]">Make default</Btn>}
                  <div className="flex items-center gap-2 text-[13px] text-stone-700">
                    <Toggle on={p.forDevelopers} onChange={(v) => patch(p.id, { forDevelopers: v })} label={`Developers can use ${p.name}`} disabled={saving} />
                    Developers can use
                  </div>
                  {!isDefault && (
                    <Btn
                      kind="ghost"
                      aria-label={`Delete ${p.name}`}
                      onClick={() => setDoc({ ...doc, profiles: doc.profiles.filter((x) => x.id !== p.id) })}
                      className="min-h-[44px]"
                    >
                      <Trash2 size={16} />
                    </Btn>
                  )}
                </div>
              </div>
              <ChoiceRow
                label="Analysing"
                hint="Ruby reads each issue once and says what is wrong and where."
                value={p.analysis}
                registry={registry}
                onChange={(c) => patch(p.id, { analysis: c })}
                disabled={saving}
              />
              <ChoiceRow
                label="Developing"
                hint="Ruby fixes the issue on its own branch (Claude Code)."
                value={p.develop}
                registry={registry}
                onChange={(c) => patch(p.id, { develop: c })}
                disabled={saving}
              />
            </div>
          );
        })}
      </Card>
    </section>
  );
};
